import React, { useState } from "react"

import { toast } from "sonner"

import { useAuth } from "@/contexts/AuthContext"
import { Depot } from "@/hooks/useDepots"
import { ukFormat } from "@/lib/ukFormat"
import { Analytics, AnalyticsEvent } from "@/services/analytics"
import { fetchApplicationsForExport } from "@/services/fetchApplicationsForExport"
import { JobPosting } from "@/types/jobPosting"
import { exportApplicationsToCSV } from "@/utils/csvExportUtils"

import { FilterState } from "../../types/application"
import ApplicationTableWithInfiniteScroll from "../ApplicationTableWithInfiniteScroll"
import FilterBar from "../FilterBar"
import KanbanBoard from "../KanbanBoard"
import ViewToggle from "../common/ViewToggle"

import ExportApplicationsButton from "./ExportApplicationsButton"

interface ApplicationsContentProps {
  viewMode: "kanban" | "table"
  locations: Depot[]
  jobPostings: JobPosting[]
  filters: FilterState
  setFilters: (filters: FilterState) => void
  setViewMode: (mode: "kanban" | "table") => void
  isLoading?: boolean
}

const ApplicationsContent: React.FC<ApplicationsContentProps> = ({
  viewMode,
  locations,
  jobPostings,
  filters,
  setFilters,
  setViewMode,
  isLoading = false,
}) => {
  const { claims } = useAuth()
  const [searchQuery, setSearchQuery] = useState<string>("")

  const canExport =
    claims?.user_role === "Admin" &&
    (claims?.user_team === "Admin" || claims?.user_team === "Central Operations")

  const handleExport = async (quarter: string) => {
    try {
      const applications = await fetchApplicationsForExport({ quarter, searchQuery, filters })
      if (applications.length === 0) {
        toast.info("No applications for that quarter")
        return
      }
      Analytics.track(AnalyticsEvent.APPLICATIONS_EXPORTED, {
        quarter,
        count: applications.length,
        applicationIds: applications.map((a) => a.applicationId),
      })
      exportApplicationsToCSV(
        applications,
        `applications_${quarter}_${ukFormat(new Date(), "yyyy-MM-dd_HHmmss")}.csv`,
      )
    } catch (err) {
      toast.error("Failed to export applications", {
        description: (err as { message?: string })?.message || "Unknown error",
      })
    }
  }

  return (
    <>
      <div className="pb-4 flex flex-wrap gap-3 items-center">
        <FilterBar
          locations={locations}
          jobPostings={jobPostings}
          filters={filters}
          setFilters={setFilters}
          onSearch={setSearchQuery}
          isLoading={isLoading}
        />
        <div className="flex items-center gap-3 ml-auto">
          {canExport && <ExportApplicationsButton onExport={handleExport} />}
          <ViewToggle
            viewMode={viewMode}
            setViewMode={setViewMode}
            toggleType="applications"
          />
        </div>
      </div>

      {viewMode === "kanban" ? (
        <KanbanBoard searchQuery={searchQuery} filters={filters} />
      ) : (
        <ApplicationTableWithInfiniteScroll searchQuery={searchQuery} filters={filters} />
      )}
    </>
  )
}

export default ApplicationsContent
